import { querySelectorNonNull } from "../../document"
import { ValidatedComponent } from "../../validation/validatedComponent"
import { validateFieldWith } from "../../validation/validateFieldWith"

export class AuthField implements ValidatedComponent {
    readonly ocAuthField: HTMLOcAuthFieldV1Element
    private readonly inputField: HTMLInputElement
    private readonly inputHint: HTMLElement
    private _shouldShowError = false
    private error?: string
    private readonly hint: string
    private readonly allowedPattern: string | null

    constructor(onAuthField: HTMLOcAuthFieldV1Element) {
        this.ocAuthField = onAuthField
        this.inputField = querySelectorNonNull(onAuthField, "input")
        this.inputHint = querySelectorNonNull(onAuthField, "div[slot]")
        const dataHintAtt = this.inputHint.getAttribute("data-hint")
        this.hint = dataHintAtt != null ? dataHintAtt : ""
        const hintContent = this.inputHint.textContent != null ? this.inputHint.textContent : undefined
        this.error = onAuthField.variant == "error" ? hintContent : undefined
        this.allowedPattern = this.inputField.getAttribute("data-fti-ci-allowed-input") ?? null
        this.validateInput()
    }

    private validateInput() {
        if (this.allowedPattern != null) {
            const regex = new RegExp(`^${this.allowedPattern}+$`)
            this.inputField.addEventListener("beforeinput", (event: InputEvent) => {
                if (event.data && !regex.test(event.data)) {
                    event.preventDefault()
                }
            })
        }
    }

    get allFields() {
        return [this.inputField]
    }

    get hasError() {
        return this.error != undefined
    }

    get value(): string {
        return this.inputField.value
    }

    set value(value: string) {
        this.inputField.value = value
    }

    set shouldShowError(shouldShowError: boolean) {
        this._shouldShowError = shouldShowError
        this.updateValidationState()
    }

    get shouldShowError() {
        return this._shouldShowError
    }

    set validationResult(error: string | undefined) {
        this.error = error
        this.updateValidationState()
    }

    get validationResult() {
        return this.error
    }

    private updateValidationState() {
        if (!this.shouldShowError || this.error == undefined) {
            this.inputHint.slot = this.hint ? "hint" : "none"
            this.inputHint.textContent = this.hint
            this.inputField.setAttribute("aria-invalid", "false")
            this.ocAuthField.variant = "default"
        } else if (this.shouldShowError) {
            const errorVariant = "error"
            this.inputHint.slot = errorVariant
            this.inputHint.textContent = this.error
            this.inputField.setAttribute("aria-invalid", "true")
            this.ocAuthField.variant = errorVariant
        }
    }
}

export function validateAuthFieldWith(
    authField: AuthField,
    validationFunction: (value: string, signal: AbortSignal) => PromiseLike<string | undefined>,
): () => void {
    return validateFieldWith(authField, (abortSignal) => validationFunction(authField.value, abortSignal))
}
